import axios, { AxiosRequestConfig } from 'axios';
import { LEGAL_VERSION } from '../constants/legal';
import { 
    UserProfile, 
    ContactReference, 
    ContactRequest, 
    UserSearchResult,
    UpdateUserProfileRequest 
} from '../types/userProfile';

/**
 * Configuration pour les requêtes API avec authentification
 */
const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    }
});

/** Code renvoyé par `GET /api/users/me` quand l'identité n'a pas de profil et que l'inscription est sur invitation. */
export const INVITATION_REQUISE = 'INVITATION_REQUISE';

/**
 * Refus typé de la porte d'invitation (13/10/2026) : le code du serveur et son
 * message, affiché tel quel. `code` vaut `INVITATION_REQUISE` (pas de profil,
 * un code est attendu), `INVITATION_INVALIDE` (code refusé), `CODE_MANQUANT`,
 * `GLOBAL_LIMIT` (inscriptions en pause) ou `INVITATION_ERREUR` (réseau).
 */
export class InvitationError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'InvitationError';
        this.code = code;
    }
}

const MESSAGE_INVITATION_REQUISE_DEFAUT = 'Anagogia est en phase de test sur invitation. Entrez votre code d\'invitation pour créer votre espace.';
const MESSAGE_INVITATION_ERREUR = 'Impossible de vérifier le code d\'invitation. Réessayez dans un instant.';

/**
 * Refus typé des gestes du compte (C27 SP2) : `CONSENTEMENT_INVALIDE` (les textes
 * ont changé), `CONFIRMATION_INVALIDE` (adresse retapée fausse), `ABONNEMENT_ACTIF`
 * (Stripe n'a pas pu résilier), `PROFIL_ABSENT`, ou `COMPTE_ERREUR` (réseau).
 */
export class CompteError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'CompteError';
        this.code = code;
    }
}
const MESSAGE_COMPTE_ERREUR = 'Impossible de joindre le serveur. Réessayez dans un instant ; rien n\'a été supprimé.';
const MESSAGE_CONSENTEMENT_ERREUR = 'Impossible d\'enregistrer votre accord pour l\'instant. Réessayez dans un instant.';

/** Résultat de `DELETE /api/users/me`. */
export interface ResultatSuppressionCompte {
    /** Tout est effacé, compte de connexion compris. */
    complet: boolean;
    /** Sort du compte de connexion : `differe` ou `non_configure` = effacé sous 24 h. */
    b2c: 'efface' | 'absent' | 'non_configure' | 'differe';
}

/**
 * Service API pour la gestion des profils utilisateurs et contacts
 */
export const userProfileService = {
    /**
     * Enregistre l'accord aux textes courants (`PUT /api/users/me/consentements`,
     * C27 SP2) : la version du client et la case des contenus sensibles. Rend le
     * profil, qui porte désormais `consentements`.
     */
    accepterConsentements: async (accessToken: string): Promise<UserProfile> => {
        try {
            const response = await axios.put('/api/users/me/consentements', { version: LEGAL_VERSION, contenusSensibles: true }, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            const data = error?.response?.data;
            const message = typeof data?.message === 'string' && data.message ? data.message : MESSAGE_CONSENTEMENT_ERREUR;
            throw new CompteError(typeof data?.code === 'string' ? data.code : 'COMPTE_ERREUR', message);
        }
    },

    /**
     * Supprime le compte et tout ce qu'il contient (`DELETE /api/users/me`, C27 SP2).
     * L'adresse retapée confirme ; un refus du serveur (400, 404, 409) porte son message.
     */
    supprimerCompte: async (email: string, accessToken: string): Promise<ResultatSuppressionCompte> => {
        try {
            const response = await axios.delete('/api/users/me', { ...createConfig(accessToken), data: { email } });
            return response.data.data;
        } catch (error: any) {
            const data = error?.response?.data;
            const message = typeof data?.message === 'string' && data.message ? data.message : MESSAGE_COMPTE_ERREUR;
            throw new CompteError(typeof data?.code === 'string' ? data.code : 'COMPTE_ERREUR', message);
        }
    },

    /**
     * Récupère le profil de l'utilisateur connecté.
     * 403 `INVITATION_REQUISE` : pas une panne — l'identité n'a pas de profil et
     * doit entrer un code d'invitation ; levé comme `InvitationError` pour que la
     * tranche montre la porte au lieu d'une erreur.
     */
    getMyProfile: async (accessToken: string): Promise<UserProfile> => {
        try {
            const response = await axios.get('/api/users/me', createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            const status = error?.response?.status;
            const data = error?.response?.data;
            if (status === 403 && data?.code === INVITATION_REQUISE) {
                throw new InvitationError(INVITATION_REQUISE, typeof data.message === 'string' && data.message ? data.message : MESSAGE_INVITATION_REQUISE_DEFAUT);
            }
            console.error('Erreur lors de la récupération du profil:', error);
            throw new Error('Impossible de récupérer le profil');
        }
    },

    /**
     * Crée l'espace sur un code d'invitation (`POST /api/users/me/invitation`,
     * 13/10/2026). Rend le profil créé, comme `GET /api/users/me`. Un refus
     * (400, 403, 503) devient une `InvitationError` qui porte le message du serveur.
     */
    accepterInvitation: async (code: string, accessToken: string): Promise<UserProfile> => {
        try {
            const response = await axios.post('/api/users/me/invitation', { code }, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            const data = error?.response?.data;
            const message = typeof data?.message === 'string' && data.message ? data.message : MESSAGE_INVITATION_ERREUR;
            throw new InvitationError(typeof data?.code === 'string' ? data.code : 'INVITATION_ERREUR', message);
        }
    },

    /**
     * Met à jour le profil de l'utilisateur connecté
     */
    updateMyProfile: async (
        data: UpdateUserProfileRequest, 
        accessToken: string
    ): Promise<UserProfile> => {
        try {
            const response = await axios.put('/api/users/me', data, createConfig(accessToken));
            return response.data.data;
        } catch (error) {
            console.error('Erreur lors de la mise à jour du profil:', error);
            throw new Error('Impossible de mettre à jour le profil');
        }
    },

    /**
     * Récupère le profil public d'un utilisateur
     */
    getUserProfile: async (userId: string, accessToken: string): Promise<UserProfile | null> => {
        try {
            const response = await axios.get(`/api/users/${userId}`, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error('Erreur lors de la récupération du profil utilisateur:', error);
            throw new Error('Impossible de récupérer le profil utilisateur');
        }
    },

    /**
     * Recherche des utilisateurs
     */
    searchUsers: async (
        query: string, 
        accessToken: string,
        options?: { page?: number; limit?: number }
    ): Promise<{ data: UserSearchResult[]; total: number }> => {
        try {
            const params = new URLSearchParams({ q: query });
            if (options?.page) params.append('page', options.page.toString());
            if (options?.limit) params.append('limit', options.limit.toString());
            
            const response = await axios.get(
                `/api/users/search?${params.toString()}`, 
                createConfig(accessToken)
            );
            return {
                data: response.data.data,
                total: response.data.total
            };
        } catch (error) {
            console.error('Erreur lors de la recherche d\'utilisateurs:', error);
            throw new Error('Impossible de rechercher des utilisateurs');
        }
    },

    /**
     * Récupère les contacts de l'utilisateur
     */
    getContacts: async (
        accessToken: string, 
        status?: string
    ): Promise<ContactReference[]> => {
        try {
            const url = status 
                ? `/api/users/contacts?status=${status}` 
                : '/api/users/contacts';
            const response = await axios.get(url, createConfig(accessToken));
            return response.data.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des contacts:', error);
            throw new Error('Impossible de récupérer les contacts');
        }
    },

    /**
     * Envoie une demande de contact
     */
    sendContactRequest: async (
        toUserId: string, 
        message: string | undefined,
        accessToken: string
    ): Promise<ContactRequest> => {
        try {
            const response = await axios.post(
                '/api/users/contacts/request',
                { toUserId, message },
                createConfig(accessToken)
            );
            return response.data.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Impossible d\'envoyer la demande de contact';
            console.error('Erreur lors de l\'envoi de la demande de contact:', error);
            throw new Error(errorMessage);
        }
    },

    /**
     * Récupère les demandes de contact
     */
    getContactRequests: async (
        accessToken: string,
        type: 'received' | 'sent' | 'all' = 'received'
    ): Promise<ContactRequest[]> => {
        try {
            const response = await axios.get(
                `/api/users/contacts/requests?type=${type}`,
                createConfig(accessToken)
            );
            return response.data.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des demandes:', error);
            throw new Error('Impossible de récupérer les demandes de contact');
        }
    },

    /**
     * Répond à une demande de contact
     */
    respondToContactRequest: async (
        requestId: string,
        accept: boolean,
        accessToken: string
    ): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await axios.post(
                `/api/users/contacts/requests/${requestId}/respond`,
                { accept },
                createConfig(accessToken)
            );
            return response.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Impossible de traiter la demande';
            console.error('Erreur lors de la réponse à la demande:', error);
            throw new Error(errorMessage);
        }
    },

    /**
     * Supprime un contact
     */
    removeContact: async (contactUserId: string, accessToken: string): Promise<void> => {
        try {
            await axios.delete(
                `/api/users/contacts/${contactUserId}`,
                createConfig(accessToken)
            );
        } catch (error) {
            console.error('Erreur lors de la suppression du contact:', error);
            throw new Error('Impossible de supprimer le contact');
        }
    },

    /**
     * Bloque un utilisateur
     */
    blockUser: async (userId: string, accessToken: string): Promise<void> => {
        try {
            await axios.post(
                `/api/users/contacts/${userId}/block`,
                {},
                createConfig(accessToken)
            );
        } catch (error) {
            console.error('Erreur lors du blocage de l\'utilisateur:', error);
            throw new Error('Impossible de bloquer l\'utilisateur');
        }
    }
};
